/**
 * The state bridge (frontend.md §1.3.1).
 *
 * Two channels, kept strictly apart:
 *
 *  - **Schema** → mirrored into `store.room`. Declarative truth. Drives what the
 *    DOM *is*, and survives reconnection.
 *  - **Messages** → private hand, legal actions, señas, chat, and the animation
 *    queue. One-shot. Drives what the DOM *animates*.
 *
 * Animating off state diffs alone cannot tell "a card moved because it was
 * dealt" from "a card moved because the window resized", and a resync would
 * replay the whole hand as a fresh deal. Hence the split.
 */

import type { Room } from 'colyseus.js';

import type { BetKind, CardId, Seat } from '@ordago/rules';
import type { MusTermKey, ServerMessageMap } from '@ordago/shared';

import { recordEvent } from '../debug';
import { director } from '../game/anim/director';
import { queueAnimations } from '../game/anim/events';
import { useStore } from '../store';
import type { Bubble, SenaFlash } from '../store/types';

import { projectState, reuseSeats, sameBetting, sameLances } from './schema';

/** How long a seña overlay and a bet bubble stay on screen. */
const SENA_MS = 1_000;
const BUBBLE_MS = 1_800;
/**
 * The one bubble that outlives its own round. A "no hay mus" ends the mus round
 * on the spot and the engine opens grande in the same breath, so at the normal
 * duration the only announcement that the round is over is already fading while
 * the first lance is being drawn. Holding it a beat longer is what lets the
 * other three seats read what happened (v1.1, playtest item 1).
 *
 * It delays nothing: the phase has already changed, the bar is already the
 * betting one, and input is never gated on a bubble.
 */
const NO_MUS_BUBBLE_MS = 2_800;
const MAX_CHAT = 200;

type Unbind = () => void;

/**
 * The schema half. Colyseus already coalesces patches at the room's patch rate
 * (20 Hz), so one store commit per `onStateChange` is exactly the batching
 * §1.3.1 asks for — a second microtask queue on top of it would only add a
 * frame of latency.
 */
function bindState(room: Room<unknown>): void {
  const apply = (state: unknown): void => {
    const view = projectState(state);
    useStore.setState((s) => {
      const seats = reuseSeats(s.room.seats, view.seats);
      const betting = sameBetting(s.room.betting, view.betting) ? s.room.betting : view.betting;
      const lances = sameLances(s.room.lances, view.lances) ? s.room.lances : view.lances;
      const mySeat = findMySeat(seats, s.auth.user?.id ?? null) ?? s.room.mySeat;

      // Safety net for the same problem `send.action` handles from the other
      // side: if the turn has moved past the seat this legality was issued for,
      // it is stale. The discard phase is exempt because it is the one
      // simultaneous phase — all four seats are armed at once and `turn` does
      // not name any of them (backend.md §3.5).
      //
      // Compared against `legalSeat`, NOT against `mySeat`. `game.legal` is a
      // message and the turn is schema state, so the two arrive on independent
      // channels and a patch describing the *previous* turn can land after the
      // message announcing this one. Testing `turnSeat !== mySeat` reads that
      // ordering as "not your turn any more" and throws away a legality that is
      // current — and since `game.legal` is one-shot with nothing to re-request
      // it with, the seat is stranded and the table sits there forever. Seen
      // once at `juegoDeclare`: the schema said turn=0 and seat 0 had no legal
      // actions at all. Against `legalSeat` the same patch is a no-op, because
      // the turn it names is the seat the legality was for.
      const staleLegal =
        s.table.legal.length > 0 &&
        s.table.legalSeat !== null &&
        view.turnSeat !== null &&
        view.turnSeat !== s.table.legalSeat &&
        view.gamePhase !== 'discard';
      if (staleLegal) queueMicrotask(clearLegal);
      // The pings are more accurate because they halve the round trip; this is
      // the floor, so the clock is never rendered against a badly-set local one.
      const clockOffset =
        pingedOffset ?? (view.serverNow > 0 ? view.serverNow - Date.now() : s.room.clockOffset);
      return {
        room: {
          ...s.room,
          code: view.code,
          phase: view.phase,
          gamePhase: view.gamePhase,
          lance: view.lance,
          config: view.config,
          leaderSeat: view.leaderSeat,
          seats,
          dealer: view.dealer,
          mano: view.mano,
          turnSeat: view.turnSeat,
          handNumber: view.handNumber,
          musRound: view.musRound,
          stockCount: view.stockCount,
          scores: view.scores,
          gamesWon: view.gamesWon,
          ranked: view.ranked,
          betting,
          lances,
          declaredPares: view.declaredPares,
          declaredJuego: view.declaredJuego,
          revealedBySeat: view.revealedBySeat as Partial<Record<Seat, CardId[]>>,
          mySeat,
          clockOffset,
        },
      };
    });
  };

  room.onStateChange(apply);
  // `onStateChange` fires on the first patch, but a room joined mid-flight can
  // already have state, and the pre-game screen must not flash empty.
  if (room.state !== undefined && room.state !== null) apply(room.state);
}

/** Deferred out of the `setState` updater, which must stay pure. */
function clearLegal(): void {
  useStore.setState((s) => ({ table: { ...s.table, legal: [], legalSeat: null } }));
}

/**
 * A seat is mine when it carries my user id. Derived rather than remembered,
 * because a seat swap in the pre-game screen moves me without a new join.
 */
function findMySeat(
  seats: { seat: Seat; userId: string | null }[],
  userId: string | null,
): Seat | null {
  if (userId === null) return null;
  return seats.find((s) => s.userId === userId)?.seat ?? null;
}

// ─────────────────────────────────────────────────────────────────── transients

function pushSena(flash: SenaFlash): void {
  useStore.setState((s) => ({ table: { ...s.table, senas: [...s.table.senas, flash] } }));
  setTimeout(() => {
    useStore.setState((s) => ({
      table: { ...s.table, senas: s.table.senas.filter((x) => x !== flash) },
    }));
  }, SENA_MS);
}

function pushBubble(bubble: Bubble, holdMs = BUBBLE_MS): void {
  useStore.setState((s) => ({
    // One bubble per seat: a raise replaces the paso that preceded it rather
    // than stacking two speech balloons on one avatar.
    table: {
      ...s.table,
      bubbles: [...s.table.bubbles.filter((b) => b.seat !== bubble.seat), bubble],
    },
  }));
  setTimeout(() => {
    useStore.setState((s) => ({
      table: { ...s.table, bubbles: s.table.bubbles.filter((b) => b !== bubble) },
    }));
  }, holdMs);
}

const BET_TERM: Record<BetKind, MusTermKey> = {
  PASO: 'paso',
  ENVIDO: 'envido',
  ORDAGO: 'ordago',
  QUIERO: 'quiero',
  NO_QUIERO: 'noQuiero',
};

// ──────────────────────────────────────────────────────────────────── messages

function bindMessages(room: Room<unknown>): void {
  const on = <K extends keyof ServerMessageMap>(
    name: K,
    handler: (payload: ServerMessageMap[K]) => void,
  ): void => {
    room.onMessage(name, handler);
  };

  // The one message carrying cards, and it only ever carries mine.
  on('hand.private', ({ cards, eval: evaluation }) => {
    useStore.getState().table.setHand(cards, evaluation);
  });

  on('game.legal', ({ seat, actions, deadlineAt, reserveMs }) => {
    const s = useStore.getState();
    if (s.room.mySeat !== null && seat !== s.room.mySeat) return;
    s.table.setLegal(seat, actions, deadlineAt, reserveMs);
  });

  // Three of the twenty event types change store state; the rest are purely
  // choreography, which `queueAnimations` folds into the director's queue in one
  // pass at the end. An `if` chain rather than a `switch` keeps that asymmetry
  // visible instead of burying it under seventeen empty cases.
  on('game.event', ({ events }) => {
    for (const event of events) {
      // Test-only ledger, so a spec can assert the score against the sum of
      // what this client was actually told rather than only that the game
      // ended. A no-op unless the debug hook is installed.
      recordEvent(event);

      if (event.type === 'BET') {
        pushBubble({
          seat: event.seat,
          term: BET_TERM[event.kind],
          ...(event.amount === undefined ? {} : { amount: event.amount }),
          at: Date.now(),
        });
      } else if (event.type === 'MUS_VOTE') {
        // The mus round is spoken out loud at a real table, and until v1.1 it
        // was the one table action that produced no bubble — every bet got one,
        // the vote got nothing. Only the seat whose turn it is is ever shown a
        // mus bar, and the round ends on the first "no hay mus", so from the
        // other three seats the whole thing happened in silence and was over
        // before it was visible. That is what the 2026-09-01 playtest reported
        // as not being able to vote mus (feedback/2026-09-01-user-playtest.md).
        const refused = event.vote !== 'mus';
        pushBubble(
          { seat: event.seat, term: refused ? 'noHayMus' : 'mus', at: Date.now() },
          refused ? NO_MUS_BUBBLE_MS : BUBBLE_MS,
        );
      } else if (event.type === 'HANDS_REVEALED') {
        const { hands, evals } = event;
        useStore.setState((s) => ({
          table: {
            ...s.table,
            reveal: { hands, evals, breakdown: s.table.reveal?.breakdown ?? [] },
          },
        }));
      } else if (event.type === 'HAND_STARTED') {
        // A new deal clears the previous hand's reveal and any stale legality.
        useStore.setState((s) => ({
          table: { ...s.table, reveal: null, legal: [], selected: [], deadlineAt: 0 },
        }));
      }
    }
    // Queued as choreography, not as twenty individual events: one `deal` for
    // sixteen cards rather than sixteen deals waiting on each other.
    queueAnimations(events, useStore.getState().room.mySeat);
  });

  on('game.reveal', ({ hands, evals, breakdown }) => {
    // The genuine cross-parent case §3.3 was written for: sixteen cards appear
    // in a subtree that did not exist a moment ago. Capturing here — before the
    // mutation, in the handler that owns it — is what lets `<FlipRoot/>` fade
    // and scale them in from the tree they replaced instead of popping.
    director.captureFlip();
    useStore.setState((s) => ({ table: { ...s.table, reveal: { hands, evals, breakdown } } }));
    director.push({ t: 'reveal' });
  });

  on('game.over', (payload) => {
    useStore.setState((s) => ({ table: { ...s.table, over: payload, legal: [] } }));
  });

  on('game.timedOut', ({ seat }) => {
    useStore.setState((s) => ({
      table: {
        ...s.table,
        substituted: s.table.substituted.includes(seat)
          ? s.table.substituted
          : [...s.table.substituted, seat],
      },
    }));
  });

  on('game.resync', ({ view }) => {
    // The private half of a resync: the schema half arrives as a normal patch.
    useStore.getState().table.setHand(view.myCards, view.myEval);
    useStore.getState().table.setLegal(useStore.getState().room.mySeat, view.legal, 0, 0);
    useStore.setState((s) => ({
      table: {
        ...s.table,
        reveal:
          view.revealed === null
            ? null
            : {
                hands: view.revealed,
                evals: s.table.reveal?.evals ?? ({} as never),
                breakdown: [],
              },
      },
    }));
  });

  on('sena.received', ({ fromSeat, sena, decoded }) => {
    pushSena({ seat: fromSeat, sena, decoded, at: Date.now() });
  });

  on('chat.message', (msg) => {
    useStore.setState((s) => {
      const chat = [...s.room.chat, msg];
      return {
        room: {
          ...s.room,
          chat: chat.length > MAX_CHAT ? chat.slice(chat.length - MAX_CHAT) : chat,
          unreadChat: s.ui.chatOpen ? 0 : s.room.unreadChat + 1,
        },
      };
    });
  });

  on('room.playerLeft', ({ seat }) => {
    useStore.setState((s) => ({
      table: {
        ...s.table,
        substituted: s.table.substituted.includes(seat)
          ? s.table.substituted
          : [...s.table.substituted, seat],
      },
    }));
  });

  on('room.playerReconnected', ({ seat }) => {
    useStore.setState((s) => ({
      table: { ...s.table, substituted: s.table.substituted.filter((x) => x !== seat) },
    }));
  });

  on('room.error', ({ code, messageKey }) => {
    useStore.setState((s) => ({
      room: { ...s.room, lastError: { code, messageKey, at: Date.now() } },
    }));
  });

  on('room.closed', () => {
    useStore.setState((s) => ({ room: { ...s.room, connection: 'closed' } }));
  });

  on('server.pong', ({ t, serverNow }) => {
    recordPing(t, serverNow);
  });
}

// ───────────────────────────────────────────────────────────────── clock offset

const samples: number[] = [];

/** Null until three pongs have landed; the schema's `serverNow` covers until then. */
let pingedOffset: number | null = null;

/**
 * Median of three, not a mean: one packet caught behind a retransmit skews an
 * average by hundreds of milliseconds, which is a whole turn on `muyRapido`.
 */
function recordPing(sentAt: number, serverNow: number): void {
  const rtt = Date.now() - sentAt;
  samples.push(serverNow + rtt / 2 - Date.now());
  if (samples.length < 3) return;
  const sorted = [...samples].sort((a, b) => a - b);
  pingedOffset = sorted[Math.floor(sorted.length / 2)] ?? 0;
  const offset = pingedOffset;
  useStore.setState((s) => ({ room: { ...s.room, clockOffset: offset } }));
}

function startPinging(room: Room<unknown>): Unbind {
  samples.length = 0;
  pingedOffset = null;
  const timers = [0, 250, 500].map((delay) =>
    setTimeout(() => {
      room.send('client.ping', { t: Date.now() });
    }, delay),
  );
  return () => {
    for (const timer of timers) clearTimeout(timer);
  };
}

// ────────────────────────────────────────────────────────────────────── public

export function bindRoom(room: Room<unknown>): Unbind {
  bindState(room);
  bindMessages(room);
  const stopPinging = startPinging(room);

  room.onError((_code, message) => {
    useStore.setState((s) => ({
      room: {
        ...s.room,
        lastError: { code: 'VALIDATION_ERROR', messageKey: message ?? '', at: Date.now() },
      },
    }));
  });

  room.onLeave((code) => {
    // 1000 is a clean close: the player left, or the room disposed. Anything
    // else is a drop, and Colyseus will be trying to reconnect underneath.
    useStore.setState((s) => ({
      room: { ...s.room, connection: code === 1000 ? 'closed' : 'reconnecting' },
    }));
  });

  return () => {
    stopPinging();
    room.removeAllListeners();
  };
}

export type { CardId };
